import type { Contact } from '@/lib/services/contacts/types';
import {
  allocateUnitsByShare,
  type ItemBeneficiaryShare
} from '@/lib/services/entitlements/specific-gift-defaults';
import type { ShareInstruction, ShareTransferBeneficiary } from './types';

/**
 * The fields one share-instruction answer writes.
 *
 * The same shape `ShareInstructionSection` commits and `shareFieldsPatch` turns
 * into a patch — declared here rather than imported from either, so this module
 * stays the rule and neither call site owns it. Deliberately identical in spirit
 * to {@link ShareOwnershipChange} in `./share-ownership`, its sibling.
 */
export interface ShareInstructionChange {
  instruction: ShareInstruction | null;
  transferBeneficiaries?: ShareTransferBeneficiary[];
}

/** Everything the seed reads. Passed in, so the rule stays pure. */
export interface ShareInstructionContext {
  /** What the holding is allocated to today — an existing allocation is never clobbered. */
  transferBeneficiaries?: ShareTransferBeneficiary[];
  /** The matter's specific-gift entitlement shares for THIS holding. */
  entitlementBeneficiaryShares?: ItemBeneficiaryShare[];
  /** The holding's unit count, which the allocation has to sum back to. */
  totalUnits?: number;
  /** The matter's contacts, for the beneficiary filter and the display names. */
  contacts?: Contact[];
}

/** Drop repeat rows for one contact, keeping the first. */
export function dedupeByContactId(
  beneficiaries: ShareTransferBeneficiary[]
): ShareTransferBeneficiary[] {
  return beneficiaries.filter(
    (tb, index) =>
      beneficiaries.findIndex((other) => other.contactId === tb.contactId) ===
      index
  );
}

/**
 * Build the default transfer-beneficiary selection from the holding's
 * specific-gift entitlement shares (LF-4121). Units are allocated across the
 * entitled contacts proportional to their share, and each row is enriched with
 * the contact's name from the matter contacts so the panel renders real names.
 * Contacts not present in the matter list (or not flagged as beneficiaries) are
 * dropped — the allocation panel only renders rows for matter beneficiaries.
 */
export function buildEntitlementSeed(
  shares: ItemBeneficiaryShare[],
  totalUnits: number | undefined,
  contacts: Contact[]
): ShareTransferBeneficiary[] {
  const beneficiaryIds = new Set(
    contacts.filter((c) => c.isBeneficiary).map((c) => c.id)
  );
  const eligibleShares = shares.filter((s) => beneficiaryIds.has(s.contactId));
  if (eligibleShares.length === 0) return [];

  const allocations = allocateUnitsByShare(totalUnits, eligibleShares);
  return allocations.map(({ contactId, units }) => {
    const contact = contacts.find((c) => c.id === contactId);
    return {
      contactId,
      contact: contact
        ? {
            id: contact.id,
            firstName: contact.firstName ?? '',
            lastName: contact.lastName ?? '',
            organisationName: contact.organisationName ?? null
          }
        : undefined,
      units
    };
  });
}

/**
 * **The seed, and the three guards that bound it** (LF-4121, LF-4068).
 *
 * Seeded only on the transition INTO `transfer-to-beneficiary`, never on
 * reopen, never over an allocation that already exists, and never for a holding
 * with no specific gift — which is what keeps a deliberately-emptied allocation
 * empty.
 *
 * `null` means "write no allocation", which is different from `[]`.
 */
function seedFor(
  ctx: ShareInstructionContext
): ShareTransferBeneficiary[] | null {
  if (dedupeByContactId(ctx.transferBeneficiaries ?? []).length > 0)
    return null;
  if (!ctx.entitlementBeneficiaryShares?.length) return null;
  const seed = buildEntitlementSeed(
    ctx.entitlementBeneficiaryShares,
    ctx.totalUnits,
    ctx.contacts ?? []
  );
  return seed.length > 0 ? seed : null;
}

/**
 * **What answering a holding's instruction must write** — the whole rule, in one
 * place (VALE-1363), and the exact sibling of
 * {@link shareOwnershipChange}'s "one rule, two answers".
 *
 * The instruction is not a lone field either: choosing
 * `transfer-to-beneficiary` seeds the allocation from the matter's specific-gift
 * entitlements, and the instruction and the allocation it seeds have to arrive
 * as ONE change. Two un-awaited commits in the same tick raced in the backend's
 * `itemInfo` merge and whichever landed last erased the other's key, silently
 * dropping the allocation a lawyer had just watched auto-fill (VALE-857).
 *
 * It lived inside `ShareInstructionSection.handleInstructionSelect`, which was
 * fine while the instruction pills were the only way to answer. The AI-extract
 * review now answers it too, as an ordinary `select` row in its detail block, so
 * a rule living in one of them is a rule the other gets wrong — which is
 * precisely the argument VALE-863 already made for ownership.
 *
 * Pure, and takes the seed's inputs rather than reading them, so an attribute
 * row can call it from a `toPatch` with no hook context.
 *
 * **One thing it deliberately does NOT do: clear the allocation on the way
 * out.** Leaving `transfer-to-beneficiary` keeps whatever was allocated, which
 * is the behaviour the pills have always had. The vehicle's rule is the
 * opposite (see `./vehicle-instruction`), and the asymmetry is real rather than
 * an oversight here: a share allocation is a unit split a lawyer may be
 * mid-way through and return to, while a vehicle's is a yes/no list that makes
 * no sense against `Sell`. Changing it is a product decision, not a
 * refactoring one, so it is preserved exactly.
 */
export function shareInstructionChange(
  next: ShareInstruction | null,
  ctx: ShareInstructionContext = {}
): ShareInstructionChange {
  if (next !== 'transfer-to-beneficiary') return { instruction: next };

  const seed = seedFor(ctx);
  return {
    instruction: next,
    ...(seed ? { transferBeneficiaries: seed } : {})
  };
}
